import { effect, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { PendoAccountInfo, PendoAccountReporter } from '../../../libraries/Ecolab.UI.Library/projects/ui-library/src/lib/features/global-search-filters/services/pendo-account-reporter.token';

/**
 * Visitor block passed to pendo.initialize(): the visitor id plus the metadata the application
 * reports about the signed in user (email, culture, persona, cloud type, ...).
 */
interface PendoVisitorOptions {
  [metadata: string]: unknown;
  id: string;
}

/** Identity Pendo is asked to record: the visitor, and the account the visitor is looking at. */
interface PendoIdentity {
  visitor: PendoVisitorOptions;
  account: PendoAccountInfo;
}

/**
 * Subset of the Pendo agent API used to report the account context.
 * `identify` is optional because the snippet in index.html first installs a stub, which queues the
 * call until the agent script has loaded and then replays it after pendo.initialize().
 */
interface PendoAgent {
  identify?: (identity: PendoIdentity) => void;
}

/** Window extended with the Pendo agent installed by the snippet in index.html. */
interface WindowWithPendo extends Window {
  pendo?: PendoAgent;
}

/**
 * Keeps the Pendo `account` block in sync with the customer hierarchy loaded on the page, so usage
 * can be analysed per site / corporate account / geo region / sales region.
 *
 * Callers resolve their own `{ id, name }` and hand it over through {@link setAccount} - the global
 * search filters use `resolvePendoAccount` for that. This service only holds the account and keeps
 * Pendo up to date: it is merged into `pendo.initialize()` on page load and reported with
 * `pendo.identify()` on every later change.
 *
 * Root-provided on purpose: the account context is application wide, so it must not be listed in an
 * NgModule `providers` array.
 */
@Injectable({
  providedIn: 'root'
})
export class PendoAnalyticsService implements PendoAccountReporter {

  /** Account currently reported to Pendo; `null` until a customer hierarchy is selected. */
  public readonly account: Signal<PendoAccountInfo | null>;

  private readonly accountState: WritableSignal<PendoAccountInfo | null> =
    signal<PendoAccountInfo | null>(null, {
      equal: (previous, current): boolean => previous?.id === current?.id && previous?.name === current?.name
    });

  /** Visitor block pendo.initialize() was called with; resent unchanged on every account change. */
  private visitorOptions: PendoVisitorOptions | null = null;

  public constructor() {
    this.account = this.accountState.asReadonly();

    // Reports every distinct account; the signal equality check keeps repeated
    // notifications for the same context point from re-sending the same account.
    effect((): void => {
      const account = this.accountState();
      if (!account) {
        return;
      }
      this.reportToPendo(account);
    });
  }

  /**
   * Updates the account context: the ContextPointId / ContextPointName of the site, corporate
   * account, geo region or sales region currently loaded on the page.
   *
   * @param account - The account block to report.
   */
  public setAccount(account: PendoAccountInfo): void {
    this.accountState.set(account);
  }

  /**
   * Records the visitor block pendo.initialize() was called with, so a later account change can be
   * reported without losing it: pendo.identify() replaces the options it is given rather than
   * merging them, so the account is appended to these options instead of standing on its own.
   *
   * @param visitor - The visitor block passed to pendo.initialize().
   */
  public setVisitorOptions(visitor: PendoVisitorOptions): void {
    this.visitorOptions = visitor;
  }

  /**
   * Reports the account to Pendo with `identify`, since switching customer hierarchy changes the
   * account identity rather than just its metadata. The visitor block is resent alongside it so the
   * signed in user and their metadata stay attached to the new account.
   *
   * @param account - The account block to report.
   */
  private reportToPendo(account: PendoAccountInfo): void {
    const visitor = this.visitorOptions;
    if (!visitor) {
      // pendo.initialize() has not run yet, so it still picks the account up itself - see
      // AppComponent.initializePendoWithSettings. Identifying here would either be skipped
      // (analytics disabled) or drop the visitor metadata initialize is about to send.
      return;
    }
    const pendo = (window as WindowWithPendo).pendo;
    if (!pendo) {
      // Snippet not present on this page (for example during PDF generation).
      return;
    }
    try {
      // On the snippet stub this is queued and replayed right after pendo.initialize(); on the
      // loaded agent it applies immediately. Either way no retry is needed.
      pendo.identify?.({
        visitor: visitor,
        account: { id: account.id, name: account.name }
      });
    } catch {
      // Analytics must never break the dashboard; the next selection change reports again.
    }
  }
}
